/**
 * Great Dragons and Great Winds — limit hands requiring a pung/kong of every
 * honor kind in one honor group. Sourced from `context.melds` (exposed
 * pung/kong) UNION `decomposeConcealedHand`'s concealed pung sets, the same
 * two-source pattern `allTriplets.ts`/`basePoints.ts` already use.
 *
 * `'limit'`-kind patterns short-circuit the whole hand to a fixed cap in
 * `scoring/ScoreCalculator.ts` (T12) — `value` here is NOT load-bearing for
 * that point math, it's purely a display label. `88` is the traditional
 * number these two patterns are commonly cited at, distinct from the generic
 * limit-hand cap T12 defines.
 */

import { SETS_TO_WIN, type Honor } from '../../game/types.js';
import { decomposeConcealedHand } from '../decompose.js';
import type { MatchedPattern, ScoringContext } from '../types.js';

const GREAT_HONORS_DISPLAY_VALUE = 88;

const DRAGON_HONORS: readonly Honor[] = ['white', 'green', 'red'];
const WIND_HONORS: readonly Honor[] = ['east', 'south', 'west', 'north'];

/** Honor kinds that have a pung or kong present, from melds + the concealed decomposition. */
function honorPungKinds(context: ScoringContext): Set<Honor> {
  const kinds = new Set<Honor>();

  for (const meldEntry of context.melds) {
    if (meldEntry.type !== 'pung' && meldEntry.type !== 'kong') continue;
    const first = meldEntry.tiles[0];
    if (first !== undefined && first.kind === 'honor') kinds.add(first.honor);
  }

  const setsNeeded = SETS_TO_WIN - context.melds.length;
  const decomposition = decomposeConcealedHand([...context.concealedTiles, context.winningTile], setsNeeded);
  if (decomposition !== null) {
    for (const set of decomposition.sets) {
      if (set.type === 'pung' && set.kind.kind === 'honor') kinds.add(set.kind.honor);
    }
  }

  return kinds;
}

export function matchGreatDragons(context: ScoringContext): MatchedPattern | null {
  const kinds = honorPungKinds(context);
  const hasAllDragons = DRAGON_HONORS.every((honor) => kinds.has(honor));
  return hasAllDragons ? { name: 'greatDragons', kind: 'limit', value: GREAT_HONORS_DISPLAY_VALUE } : null;
}

export function matchGreatWinds(context: ScoringContext): MatchedPattern | null {
  const kinds = honorPungKinds(context);
  const hasAllWinds = WIND_HONORS.every((honor) => kinds.has(honor));
  return hasAllWinds ? { name: 'greatWinds', kind: 'limit', value: GREAT_HONORS_DISPLAY_VALUE } : null;
}
